import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { buildPermissionsFromApiCodes } from "../permissions/buildPermissionsFromApi";
import { resolveRoleKind } from "../permissions/resolvePermissions";

/**
 * Prefer `me.role.name`; if the API sends an empty `role` object, fall back to `me.jobTitle`
 * (e.g. jobTitle "Developer" with `role: {}`).
 */
function hasRoleName(r) {
  if (r == null || typeof r !== "object") return false;
  const n = r.name;
  return n != null && String(n).trim() !== "";
}

function roleForPermissions(me) {
  const r = me?.role;
  if (hasRoleName(r)) return r;
  const jt = me?.jobTitle;
  if (jt != null && String(jt).trim() !== "") {
    return { ...(r && typeof r === "object" ? r : {}), name: String(jt).trim() };
  }
  return r ?? null;
}

/**
 * Permissions from GET /users/me only: `role.permissions` API codes → `true`; all other app keys → `false`.
 *
 * @returns {{ can: (key: string) => boolean, permissions: Record<string, boolean>, role: object|null, roleKind: string|null }}
 */
export function usePermissions() {
  const me = useSelector((state) => state.auth?.me ?? null);
  const role = useMemo(() => roleForPermissions(me), [me]);

  const roleKind = useMemo(() => resolveRoleKind(role), [role]);
  const permissions = useMemo(() => buildPermissionsFromApiCodes(role?.permissions), [role]);

  const can = useCallback((key) => permissions[key] === true, [permissions]);

  return { can, permissions, role, roleKind };
}
